/**
 * Event-Based Campaign Templates
 * Based on best practices from ON24, Hopin, Zoom Events, Demandbase
 *
 * Key insights from event marketing leaders:
 * - Event-triggered outreach has 3x higher response rates
 * - 60% of webinar registrants who don't attend still engage with recordings
 * - Conference follow-up within 24 hours sees 65% higher engagement
 * - Content offers with urgency see 40% higher conversion
 * - Workshop invites to smaller groups have 2x attendance rates
 */

import { CampaignTemplate } from "./types";

export const EVENT_WEBINAR_INVITE: CampaignTemplate = {
  id: "event-webinar-invite",
  name: "Webinar Invitation Sequence",
  category: "event",
  description: "Drive webinar registrations with a proven 4-email sequence.",
  strategy:
    "Based on ON24's webinar best practices showing that multiple reminder touchpoints increase attendance rates by 40%. Sequence includes initial invite, reminder, last-chance, and post-event follow-up for both attendees and no-shows.",
  icon: "VideoCameraOutlined",
  color: "#f59e0b",
  targetAudience: "b2b-saas",
  personalizationLevel: "basic",

  expectedOutcomes: [
    "25-40% registration rate",
    "35-45% of registrants attend live",
    "60%+ watch recording if missed",
    "Pipeline from engaged attendees",
  ],

  bestPractices: [
    "Send initial invite 2-3 weeks before event",
    "Include clear date/time with timezone",
    "Highlight speakers and their credentials",
    "Create urgency with limited spots messaging",
    "Follow up with recording within 24 hours",
  ],

  objectionHandling: [
    "'I'm busy that day' → Promise to send recording, still encourage registration",
    "'Not relevant to me' → Share specific agenda point that matches their role",
    "'Too many webinars' → Differentiate with unique angle or speaker",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "purchased", "event"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["Technology", "Marketing", "E-commerce", "Professional Services"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Initial Invitation",
      subject: "[Webinar] {{webinar_title}} - {{webinar_date}}",
      suggestedMjmlTemplateId: "coldOutreach-webinarInvite",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Initial webinar invitation with full details and registration CTA",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "webinar_title",
          defaultValue: "How to Scale Customer Engagement with AI",
          description: "Name of the webinar",
        },
        {
          key: "webinar_date",
          defaultValue: "January 25, 2024",
          description: "Date of the webinar",
        },
        {
          key: "webinar_time",
          defaultValue: "11:00 AM EST",
          description: "Time with timezone",
        },
        {
          key: "speaker_name",
          defaultValue: "Sarah Johnson",
          description: "Main speaker name",
        },
        {
          key: "speaker_title",
          defaultValue: "VP of Customer Success at TechCorp",
          description: "Speaker credentials",
        },
        {
          key: "registration_url",
          defaultValue: "{{asset_url}}",
          description: "Webinar registration link",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "You're invited: {{webinar_title}}",
          "{{first_name}}, join us for {{webinar_title}}",
          "Free webinar: {{webinar_title}} ({{webinar_date}})",
        ],
      },
    },
    {
      name: "Day 5 - Reminder + Agenda",
      subject: "What we'll cover: {{webinar_title}}",
      suggestedMjmlTemplateId: "coldOutreach-webinarAgenda",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Reminder with detailed agenda and takeaways",
      variables: [
        {
          key: "agenda_point_1",
          defaultValue: "The state of customer engagement in 2024",
          description: "First agenda item",
        },
        {
          key: "agenda_point_2",
          defaultValue: "AI strategies that actually work",
          description: "Second agenda item",
        },
        {
          key: "agenda_point_3",
          defaultValue: "Live Q&A with industry experts",
          description: "Third agenda item",
        },
      ],
    },
    {
      name: "Day 10 - Last Chance",
      subject: "Last chance: {{webinar_title}} is tomorrow",
      suggestedMjmlTemplateId: "coldOutreach-webinarLastChance",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Final reminder with urgency and limited spots",
    },
    {
      name: "Day 12 - Recording Follow-up",
      subject: "Recording ready: {{webinar_title}}",
      suggestedMjmlTemplateId: "coldOutreach-webinarRecording",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Share recording and offer follow-up conversation",
      variables: [
        {
          key: "recording_url",
          defaultValue: "{{asset_url}}",
          description: "Link to webinar recording",
        },
        {
          key: "highlights",
          defaultValue: "key insights on AI automation",
          description: "Brief highlight of content",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 80,
    dailyLimit: 250,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name",
      source: "crm",
    },
    {
      key: "webinar_title",
      defaultValue: "AI-Powered Customer Engagement",
      description: "Webinar name",
      source: "manual",
    },
    {
      key: "webinar_date",
      defaultValue: "TBD",
      description: "Event date",
      source: "manual",
    },
    {
      key: "registration_url",
      defaultValue: "{{asset_url}}",
      description: "Registration link",
      source: "manual",
    },
  ],

  tags: ["event", "webinar", "registration", "virtual-event", "content"],
  bestFor: ["Lead Generation", "Thought Leadership", "Product Marketing"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["ON24", "Zoom Events", "Demandbase"],
};

export const EVENT_CONFERENCE_CONNECT: CampaignTemplate = {
  id: "event-conference-connect",
  name: "Conference Connect Sequence",
  category: "event",
  description:
    "Connect with prospects before, during, and after conferences.",
  strategy:
    "Based on Demandbase's event-based ABM approach. Start outreach 2-3 weeks before the event, during the event, and within 48 hours after. Conference context provides warm conversation opener and shared experience.",
  icon: "GlobalOutlined",
  color: "#8b5cf6",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "40%+ open rate (conference context)",
    "15-25% meeting acceptance",
    "Face-to-face meetings at event",
    "Strong post-event pipeline",
  ],

  bestPractices: [
    "Research their conference agenda and sessions",
    "Offer specific value at the event (meeting, intro, resource)",
    "Follow up within 24-48 hours after event",
    "Reference specific conversations or sessions",
    "Connect on LinkedIn during/after event",
  ],

  segmentation: {
    leadSource: ["event", "linkedin", "website"],
    companySize: ["51-200", "201-500", "501-1000", "1000+"],
    industries: ["Technology", "Marketing", "Sales", "Customer Success"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Pre-Event - Intro",
      subject: "See you at {{conference_name}}?",
      suggestedMjmlTemplateId: "coldOutreach-preConference",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Pre-event outreach to schedule meeting at conference",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "conference_name",
          defaultValue: "SaaStr Annual",
          description: "Name of the conference",
        },
        {
          key: "conference_date",
          defaultValue: "September 10-12",
          description: "Conference dates",
        },
        {
          key: "booth_number",
          defaultValue: "Booth #247",
          description: "Your booth location if applicable",
        },
        {
          key: "meeting_offer",
          defaultValue: "coffee at the event",
          description: "What you're offering",
        },
      ],
    },
    {
      name: "Pre-Event - Value Add",
      subject: "My top picks for {{conference_name}}",
      suggestedMjmlTemplateId: "coldOutreach-conferenceValue",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share valuable conference insights/recommendations",
      variables: [
        {
          key: "session_recommendation",
          defaultValue: "the AI panel on Day 2",
          description: "Recommended session",
        },
      ],
    },
    {
      name: "During Event - Connect",
      subject: "{{first_name}}, let's connect today",
      suggestedMjmlTemplateId: "coldOutreach-duringConference",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Day-of outreach to meet at the event",
      variables: [
        {
          key: "your_location",
          defaultValue: "I'll be at the keynote this morning",
          description: "Where to find you",
        },
      ],
    },
    {
      name: "Post-Event - Follow-up",
      subject: "Great connecting at {{conference_name}}",
      suggestedMjmlTemplateId: "coldOutreach-postConference",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Post-event follow-up to continue conversation",
      variables: [
        {
          key: "conference_highlight",
          defaultValue: "the keynote on AI transformation",
          description: "Memorable moment to reference",
        },
        {
          key: "next_step",
          defaultValue: "continue our conversation with a quick call",
          description: "Proposed next action",
        },
      ],
    },
    {
      name: "Post-Event - Resource",
      subject: "As promised: {{resource_name}}",
      suggestedMjmlTemplateId: "coldOutreach-postConferenceResource",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Deliver promised resource and schedule meeting",
      variables: [
        {
          key: "resource_name",
          defaultValue: "the AI implementation guide we discussed",
          description: "Resource to share",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 40,
    dailyLimit: 120,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name",
      source: "crm",
    },
    {
      key: "conference_name",
      defaultValue: "Industry Conference",
      description: "Name of the event",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["event", "conference", "in-person", "networking", "trade-show"],
  bestFor: ["Field Sales", "Enterprise Sales", "Event Marketing"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Demandbase", "6sense", "SaaStr"],
};

export const EVENT_CONTENT_OFFER: CampaignTemplate = {
  id: "event-content-offer",
  name: "Content Offer Campaign",
  category: "event",
  description: "Drive downloads of gated content with a compelling sequence.",
  strategy:
    "Based on HubSpot's content marketing approach. Lead with value by offering high-quality ungated content, then follow up with related resources. Creates engagement without hard selling.",
  icon: "FileTextOutlined",
  color: "#10b981",
  targetAudience: "generic",
  personalizationLevel: "basic",

  expectedOutcomes: [
    "30-40% open rate",
    "15-25% click-through rate",
    "10-15% content download rate",
    "MQL generation from engaged readers",
  ],

  bestPractices: [
    "Lead with genuinely valuable, ungated content",
    "Make the benefit clear in subject line",
    "Include preview/teaser of content",
    "Follow up with related resources",
    "Don't gate everything - build trust first",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "purchased", "scraped"],
    companySize: ["11-50", "51-200", "201-500"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Day 0 - Content Offer",
      subject: "Free guide: {{content_title}}",
      suggestedMjmlTemplateId: "coldOutreach-contentOffer",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Initial content offer with clear value proposition",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday", "thursday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "content_title",
          defaultValue: "The Ultimate Guide to AI Chatbots in 2024",
          description: "Name of the content asset",
        },
        {
          key: "content_benefit",
          defaultValue: "Learn the strategies top companies use to automate 80% of customer inquiries",
          description: "Main benefit of reading",
        },
        {
          key: "content_url",
          defaultValue: "{{asset_url}}",
          description: "Download/view link",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "[Free] {{content_title}}",
          "{{first_name}}, thought you'd want this",
          "New research: {{content_title}}",
        ],
      },
    },
    {
      name: "Day 3 - Key Insight",
      subject: "The #1 takeaway from {{content_title}}",
      suggestedMjmlTemplateId: "coldOutreach-contentInsight",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share key insight from content to re-engage",
      variables: [
        {
          key: "key_insight",
          defaultValue: "Companies using AI chatbots see 40% reduction in support costs",
          description: "Main insight from content",
        },
      ],
    },
    {
      name: "Day 7 - Related Resource",
      subject: "You might also like: {{related_content}}",
      suggestedMjmlTemplateId: "coldOutreach-relatedContent",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Offer related content to keep engagement",
      variables: [
        {
          key: "related_content",
          defaultValue: "ROI Calculator for Chatbot Implementation",
          description: "Related resource name",
        },
        {
          key: "related_url",
          defaultValue: "{{asset_url}}",
          description: "Related resource link",
        },
      ],
    },
    {
      name: "Day 12 - Discussion Offer",
      subject: "Want to discuss {{content_topic}}?",
      suggestedMjmlTemplateId: "coldOutreach-contentDiscussion",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Transition from content to conversation",
      variables: [
        {
          key: "content_topic",
          defaultValue: "implementing these strategies at {{company_name}}",
          description: "Topic for discussion",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 70,
    dailyLimit: 200,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name",
      source: "crm",
    },
    {
      key: "content_title",
      defaultValue: "Industry Guide",
      description: "Content asset name",
      source: "manual",
    },
    {
      key: "content_url",
      defaultValue: "{{asset_url}}",
      description: "Content download link",
      source: "manual",
    },
    {
      key: "calendar_link",
      defaultValue: "{{booking_link}}",
      description: "Meeting booking link",
      source: "crm",
    },
  ],

  tags: ["event", "content", "gated-content", "lead-gen", "nurture"],
  bestFor: ["Content Marketing", "Demand Generation", "Lead Nurturing"],
  difficulty: "beginner",
  setupTime: "15 minutes",
  inspiredBy: ["HubSpot", "Marketo", "Content Marketing Institute"],
};

export const EVENT_PRODUCT_LAUNCH: CampaignTemplate = {
  id: "event-product-launch",
  name: "Product Launch Announcement",
  category: "event",
  description: "Announce new product or feature launches to prospects.",
  strategy:
    "Based on Apple and Salesforce's product launch playbook. Build anticipation before launch, make a splash on launch day, and follow up with use cases. Creates urgency through exclusivity and early adopter benefits.",
  icon: "RocketOutlined",
  color: "#3b82f6",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "40-50% open rate (newsworthiness)",
    "High engagement with launch content",
    "Demo requests from interested prospects",
    "PR and word-of-mouth amplification",
  ],

  bestPractices: [
    "Build anticipation with teaser email",
    "Focus on customer benefits, not features",
    "Include early adopter incentive",
    "Share social proof from beta users",
    "Create urgency with launch pricing",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "referral", "event"],
    companySize: ["11-50", "51-200", "201-500", "501-1000"],
    industries: ["Technology", "E-commerce", "Marketing", "Professional Services"],
    requiredFields: ["first_name", "company_name", "email"],
  },

  emails: [
    {
      name: "Pre-Launch - Teaser",
      subject: "Something big is coming...",
      suggestedMjmlTemplateId: "coldOutreach-productTeaser",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Build anticipation for upcoming launch",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "teaser_hint",
          defaultValue: "We've been working on something that will change how you engage customers",
          description: "Hint about what's coming",
        },
        {
          key: "launch_date",
          defaultValue: "January 15",
          description: "When the product launches",
        },
      ],
    },
    {
      name: "Launch Day - Announcement",
      subject: "It's here: {{product_name}}",
      suggestedMjmlTemplateId: "coldOutreach-productLaunch",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Main launch announcement with full details",
      variables: [
        {
          key: "product_name",
          defaultValue: "AI Conversation Engine 2.0",
          description: "Name of product/feature",
        },
        {
          key: "key_benefit_1",
          defaultValue: "50% faster response times",
          description: "First key benefit",
        },
        {
          key: "key_benefit_2",
          defaultValue: "AI that learns from every conversation",
          description: "Second key benefit",
        },
        {
          key: "key_benefit_3",
          defaultValue: "One-click integration with your stack",
          description: "Third key benefit",
        },
        {
          key: "demo_url",
          defaultValue: "{{booking_link}}",
          description: "Demo booking link",
        },
      ],
      abTestSuggestions: {
        subjectVariants: [
          "Introducing {{product_name}}",
          "{{first_name}}, you're going to love this",
          "NEW: {{product_name}} is live",
        ],
      },
    },
    {
      name: "Launch +2 - Use Cases",
      subject: "5 ways to use {{product_name}}",
      suggestedMjmlTemplateId: "coldOutreach-productUseCases",
      delayAfterPrevious: { value: 2, unit: "days" },
      purpose: "Share specific use cases relevant to prospect",
      variables: [
        {
          key: "use_case_1",
          defaultValue: "Automate FAQ responses",
          description: "First use case",
        },
        {
          key: "use_case_2",
          defaultValue: "Qualify leads 24/7",
          description: "Second use case",
        },
        {
          key: "use_case_3",
          defaultValue: "Book meetings automatically",
          description: "Third use case",
        },
      ],
    },
    {
      name: "Launch +5 - Early Results",
      subject: "Early adopters are seeing {{result}}",
      suggestedMjmlTemplateId: "coldOutreach-productResults",
      delayAfterPrevious: { value: 3, unit: "days" },
      purpose: "Share early results from beta/early adopters",
      variables: [
        {
          key: "result",
          defaultValue: "40% more qualified leads",
          description: "Result from early users",
        },
        {
          key: "early_adopter_quote",
          defaultValue: "This has transformed our lead generation",
          description: "Quote from early user",
        },
      ],
    },
    {
      name: "Launch +10 - Last Chance",
      subject: "Launch pricing ends Friday",
      suggestedMjmlTemplateId: "coldOutreach-productUrgency",
      delayAfterPrevious: { value: 5, unit: "days" },
      purpose: "Create urgency with expiring launch offer",
      variables: [
        {
          key: "offer_expiry",
          defaultValue: "Friday at midnight",
          description: "When offer expires",
        },
        {
          key: "offer_details",
          defaultValue: "20% off annual plans",
          description: "Launch offer details",
        },
      ],
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 60,
    dailyLimit: 180,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name",
      source: "crm",
    },
    {
      key: "product_name",
      defaultValue: "New Feature",
      description: "Product/feature name",
      source: "manual",
    },
    {
      key: "demo_url",
      defaultValue: "{{booking_link}}",
      description: "Demo booking link",
      source: "crm",
    },
  ],

  tags: ["event", "product-launch", "announcement", "new-feature", "urgency"],
  bestFor: ["Product Marketing", "New Feature Launches", "Major Updates"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Apple", "Salesforce", "Product Hunt"],
};

export const EVENT_WORKSHOP_INVITATION: CampaignTemplate = {
  id: "event-workshop-invitation",
  name: "Workshop Invitation Sequence",
  category: "event",
  description:
    "Invite prospects to hands-on workshops with limited availability.",
  strategy:
    "Based on research showing that hands-on workshops convert 3x better than passive webinars. Smaller group size creates exclusivity and enables personalized attention. Attendees become highly qualified prospects.",
  icon: "ToolOutlined",
  color: "#ec4899",
  targetAudience: "b2b-saas",
  personalizationLevel: "moderate",

  expectedOutcomes: [
    "Higher engagement than webinars",
    "50%+ attendee-to-opportunity rate",
    "Stronger relationships with attendees",
    "Hands-on product experience",
  ],

  bestPractices: [
    "Limit spots (10-20 max) for exclusivity",
    "Require some qualification for registration",
    "Provide hands-on materials/worksheets",
    "Follow up with personalized recommendations",
    "Offer 1:1 follow-up session option",
  ],

  segmentation: {
    leadSource: ["website", "linkedin", "referral"],
    companySize: ["11-50", "51-200", "201-500"],
    industries: ["Technology", "E-commerce", "Marketing", "SaaS"],
    jobTitles: [
      "Manager",
      "Director",
      "VP",
      "Head of",
      "Specialist",
      "Lead",
    ],
    requiredFields: ["first_name", "company_name", "email", "job_title"],
  },

  emails: [
    {
      name: "Day 0 - Workshop Invitation",
      subject: "[Limited] {{workshop_name}} - Only {{spots}} spots",
      suggestedMjmlTemplateId: "coldOutreach-workshopInvite",
      delayAfterPrevious: { value: 0, unit: "days" },
      purpose: "Initial workshop invitation emphasizing exclusivity",
      optimalSendTime: {
        dayOfWeek: ["tuesday", "wednesday"],
        timeSlot: "morning",
      },
      variables: [
        {
          key: "workshop_name",
          defaultValue: "AI Chatbot Masterclass",
          description: "Name of the workshop",
        },
        {
          key: "spots",
          defaultValue: "15",
          description: "Number of available spots",
        },
        {
          key: "workshop_date",
          defaultValue: "February 1, 2024",
          description: "Workshop date",
        },
        {
          key: "workshop_time",
          defaultValue: "1:00 PM - 3:00 PM EST",
          description: "Workshop time",
        },
        {
          key: "what_youll_learn",
          defaultValue: "Build and deploy your first AI chatbot",
          description: "Main takeaway",
        },
        {
          key: "registration_url",
          defaultValue: "{{asset_url}}",
          description: "Registration link",
        },
      ],
    },
    {
      name: "Day 4 - What You'll Build",
      subject: "What you'll build in {{workshop_name}}",
      suggestedMjmlTemplateId: "coldOutreach-workshopDetails",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Share specific hands-on activities and outcomes",
      variables: [
        {
          key: "hands_on_1",
          defaultValue: "Configure AI responses for your top FAQs",
          description: "First hands-on activity",
        },
        {
          key: "hands_on_2",
          defaultValue: "Set up lead qualification flows",
          description: "Second hands-on activity",
        },
        {
          key: "hands_on_3",
          defaultValue: "Deploy to your website (live!)",
          description: "Third hands-on activity",
        },
      ],
    },
    {
      name: "Day 8 - Spots Filling",
      subject: "{{spots_left}} spots left for {{workshop_name}}",
      suggestedMjmlTemplateId: "coldOutreach-workshopUrgency",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Create urgency with remaining spots",
      variables: [
        {
          key: "spots_left",
          defaultValue: "5",
          description: "Remaining spots",
        },
      ],
    },
    {
      name: "Day 12 - Last Call",
      subject: "Final call: {{workshop_name}} is tomorrow",
      suggestedMjmlTemplateId: "coldOutreach-workshopLastCall",
      delayAfterPrevious: { value: 4, unit: "days" },
      purpose: "Final reminder day before workshop",
    },
  ],

  sendingConfig: {
    emailsPerDayPerSender: 50,
    dailyLimit: 150,
    requiresWarming: true,
  },

  variables: [
    {
      key: "sender_name",
      defaultValue: "{{user_name}}",
      description: "Sender's name",
      source: "crm",
    },
    {
      key: "workshop_name",
      defaultValue: "Hands-on Workshop",
      description: "Workshop name",
      source: "manual",
    },
    {
      key: "registration_url",
      defaultValue: "{{asset_url}}",
      description: "Registration link",
      source: "manual",
    },
  ],

  tags: ["event", "workshop", "hands-on", "training", "limited-spots"],
  bestFor: ["Technical Products", "Complex Solutions", "High-Touch Sales"],
  difficulty: "intermediate",
  setupTime: "20 minutes",
  inspiredBy: ["Datadog", "Snowflake", "AWS"],
};

export const EVENT_TEMPLATES = [
  EVENT_WEBINAR_INVITE,
  EVENT_CONFERENCE_CONNECT,
  EVENT_CONTENT_OFFER,
  EVENT_PRODUCT_LAUNCH,
  EVENT_WORKSHOP_INVITATION,
];
